import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["backdrop", "input", "modeButton", "modeField", "modeLabel", "modeOption", "modeSheet"]
  static values = { mode: { type: String, default: "specific" } }

  static modes = ["specific", "cards", "decks", "articles", "tournaments"]
  static modeLabels = {
    specific: "Printings",
    cards: "Card Names",
    decks: "Decks",
    articles: "Articles",
    tournaments: "Tournaments",
  }

  connect() {
    const fieldMode = this.hasModeFieldTarget ? this.modeFieldTarget.value : null
    if (this.isValidMode(fieldMode)) {
      this.modeValue = fieldMode
    }
    const cookieMode = this.readCookie("search_mode")
    if (this.isValidMode(cookieMode)) {
      this.modeValue = cookieMode
    }
    this.applyMode()
    this.syncInputBeforeSubmit = this.syncInputBeforeSubmit.bind(this)
    this.element.addEventListener("submit", this.syncInputBeforeSubmit, true)
  }

  disconnect() {
    clearTimeout(this.focusOutTimer)
    clearTimeout(this.dropdownPositionTimer)
    this.element.removeEventListener("submit", this.syncInputBeforeSubmit, true)
    document.body.classList.remove("layout-search-active")
  }

  toggle(event) {
    event?.preventDefault()
    if (event?.type === "click" && this.skipNextModeButtonClick) {
      this.skipNextModeButtonClick = false
      return
    }
    if (event?.type === "pointerdown") {
      this.skipNextModeButtonClick = true
    }
    if (this.hasModeSheetTarget) {
      this.expand()
      if (this.isModeSheetOpen()) {
        this.closeModeSheet()
      } else {
        this.openModeSheet()
      }
      return
    }
    this.setMode(this.nextMode())
  }

  select(event) {
    event?.preventDefault()
    if (event?.type === "click" && this.skipNextModeOptionClick) {
      this.skipNextModeOptionClick = false
      return
    }
    if (event?.type === "pointerdown") {
      this.skipNextModeOptionClick = true
    }
    const mode = event.params.mode
    if (this.isValidMode(mode)) {
      this.setMode(mode)
      this.closeModeSheet()
      this.focusInput()
    }
  }

  expand(event) {
    this.scheduleAutocompleteDropdownPosition()
    // Refocusing the search input while the mode picker is open returns to the
    // search state. Focus moving onto the picker itself (its option buttons)
    // or the mode button must keep the sheet open, otherwise the sheet closes
    // mid-tap and the option's click never lands.
    if (this.isModeSheetOpen() && event?.target instanceof Element &&
        !this.modeSheetTarget.contains(event.target) &&
        (!this.hasModeButtonTarget || !this.modeButtonTarget.contains(event.target))) {
      this.closeModeSheet()
    }
    if (!this.isMobile()) return
    this.closeMobileNavMenu()
    this.element.classList.add("layout-searchbar-expanded")
    document.body.classList.add("layout-search-active")
  }

  collapse(event) {
    event?.preventDefault()
    clearTimeout(this.focusOutTimer)
    this.closeModeSheet()
    this.element.classList.remove("layout-searchbar-expanded")
    document.body.classList.remove("layout-search-active")
    this.blurInput()
  }

  closeWithKeyboard(event) {
    if (event.key !== "Escape") return
    if (this.isModeSheetOpen()) {
      event.preventDefault()
      this.closeModeSheet()
      return
    }
    if (this.isExpanded()) {
      this.collapse(event)
    }
  }

  // Any press inside the widget (mode button, submit button, autocomplete)
  // briefly blurs the input; remember it so handleFocusOut does not treat it
  // as leaving the search.
  markPointerDown() {
    this.lastInsidePointerDown = performance.now()
  }

  outsidePointerDown(event) {
    const target = event.target
    if (target instanceof Element &&
        (this.element.contains(target) || target.closest(".ts-dropdown"))) return
    if (this.isModeSheetOpen()) {
      this.closeModeSheet()
    }
    if (!this.isMobile() || !this.isExpanded()) return
    this.collapse()
  }

  handleFocusOut() {
    if (!this.isMobile() || !this.isExpanded()) return
    clearTimeout(this.focusOutTimer)
    this.focusOutTimer = setTimeout(() => {
      if (!this.isExpanded() || this.isModeSheetOpen()) return
      if (this.lastInsidePointerDown && performance.now() - this.lastInsidePointerDown < 700) return
      const active = document.activeElement
      if (active && active !== document.body &&
          (this.element.contains(active) || active.closest(".ts-dropdown"))) return
      this.collapse()
    }, 120)
  }

  isExpanded() {
    return this.element.classList.contains("layout-searchbar-expanded")
  }

  focusInput() {
    if (!this.hasInputTarget) return
    if (this.inputTarget.tomselect) {
      this.inputTarget.tomselect.focus()
    } else {
      this.inputTarget.focus()
    }
  }

  blurInput() {
    if (!this.hasInputTarget) return
    if (this.inputTarget.tomselect) {
      this.inputTarget.tomselect.blur()
    } else {
      this.inputTarget.blur()
    }
  }

  setMode(mode) {
    this.modeValue = mode
    this.writeCookie("search_mode", this.modeValue)
    this.clearInput()
    this.applyMode()
  }

  clearInput() {
    if (!this.hasInputTarget) return
    const input = this.inputTarget
    if (input.tomselect) {
      input.tomselect.clear(true)
      input.tomselect.setTextboxValue('')
    }
    input.value = ''
  }

  closeAutocompleteDropdown() {
    if (!this.hasInputTarget || !this.inputTarget.tomselect) return
    const tomselect = this.inputTarget.tomselect
    tomselect.close()
    tomselect.blur()
    tomselect.dropdown?.classList.remove("active")
  }

  syncInputBeforeSubmit() {
    if (this.hasModeFieldTarget) {
      this.modeFieldTarget.value = this.modeValue
    }
    if (!this.hasInputTarget) return

    const input = this.inputTarget
    const tomselect = input.tomselect
    const typedText = tomselect?.control_input?.value?.trim()
    const trackedText = input.dataset.tomSelectCurrentQuery?.trim()
    const committedValue = input.dataset.tomSelectCommittedValue?.trim()

    if (typedText) {
      input.value = typedText
    } else if (trackedText) {
      input.value = trackedText
    } else if (committedValue) {
      input.value = committedValue
    }
  }

  applyMode() {
    if (this.hasModeFieldTarget) {
      this.modeFieldTarget.value = this.modeValue
    }
    if (this.hasModeLabelTarget) {
      this.modeLabelTarget.textContent = this.constructor.modeLabels[this.modeValue] || "Printings"
    }
    if (this.hasModeButtonTarget) {
      this.modeButtonTarget.setAttribute("aria-expanded", this.isModeSheetOpen() ? "true" : "false")
    }
    this.updateModeOptions()
    if (this.hasInputTarget) {
      this.reinitAutocomplete()
    }
  }

  reinitAutocomplete() {
    const input = this.inputTarget
    if (this.modeValue === "cards") {
      window.initSimpleCardAutocomplete(input, { submitOnSelect: true, directCardNavigation: true })
    } else if (this.modeValue === "decks") {
      window.initDeckAutocomplete(input, { submitOnSelect: true })
    } else if (this.modeValue === "articles") {
      window.initArticleAutocomplete(input, { submitOnSelect: true })
    } else if (this.modeValue === "tournaments") {
      window.initTournamentAutocomplete(input, { submitOnSelect: true })
    } else {
      window.initCardIdAutocomplete(input, { submitOnSelect: true })
    }
    if (input.classList.contains('layout-search-input') && input.tomselect) {
      input.tomselect.dropdown.classList.add('navbar-search-dropdown')
      input.tomselect.dropdown.dataset.bsTheme = 'dark'
    }
    this.bindAutocompleteDropdownPosition(input.tomselect)
  }

  bindAutocompleteDropdownPosition(tomselect) {
    if (!tomselect || tomselect._navSearchDropdownPositionBound) return
    tomselect.on("dropdown_open", () => {
      // A remote response can reopen Tom Select after scroll dismissal because
      // the input deliberately retains focus. Keep a hidden mobile nav closed.
      const nav = this.element.closest("[data-mobile-scroll-nav]")
      if (this.isMobile() && nav?.classList.contains("nav-hidden")) {
        tomselect.close(false)
        return
      }
      this.scheduleAutocompleteDropdownPosition()
    })
    tomselect.on("load", () => this.scheduleAutocompleteDropdownPosition())
    tomselect._navSearchDropdownPositionBound = true
  }

  scheduleAutocompleteDropdownPosition() {
    this.positionAutocompleteDropdown()
    requestAnimationFrame(() => this.positionAutocompleteDropdown())
    clearTimeout(this.dropdownPositionTimer)
    this.dropdownPositionTimer = setTimeout(() => this.positionAutocompleteDropdown(), 190)
  }

  positionAutocompleteDropdown() {
    if (!this.hasInputTarget) return
    const tomselect = this.inputTarget.tomselect
    if (!tomselect || typeof tomselect.positionDropdown !== "function") return
    if (tomselect.isOpen || tomselect.dropdown?.classList.contains("active")) {
      tomselect.positionDropdown()
    }
  }

  readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
  }

  writeCookie(name, value) {
    const encodedValue = encodeURIComponent(value)
    const maxAge = 365 * 24 * 60 * 60
    document.cookie = `${name}=${encodedValue};path=/;max-age=${maxAge};SameSite=Lax`

    const parentDomain = this.cookieParentDomain()
    if (parentDomain) {
      document.cookie = `${name}=${encodedValue};path=/;domain=${parentDomain};max-age=${maxAge};SameSite=Lax`
    }
  }

  cookieParentDomain() {
    const hostname = window.location.hostname
    if (hostname === "mtggoldfish.com" || hostname.endsWith(".mtggoldfish.com")) {
      return ".mtggoldfish.com"
    }
    return null
  }

  openModeSheet() {
    if (!this.hasModeSheetTarget) return
    this.closeAutocompleteDropdown()
    this.modeSheetTarget.classList.add("is-open")
    this.modeSheetTarget.setAttribute("aria-hidden", "false")
    this.element.classList.add("nav-search-mode-open")
    if (this.hasModeButtonTarget) this.modeButtonTarget.setAttribute("aria-expanded", "true")
    this.updateModeOptions()
  }

  closeModeSheet() {
    if (!this.hasModeSheetTarget) return
    this.modeSheetTarget.classList.remove("is-open")
    this.modeSheetTarget.setAttribute("aria-hidden", "true")
    this.element.classList.remove("nav-search-mode-open")
    if (this.hasModeButtonTarget) this.modeButtonTarget.setAttribute("aria-expanded", "false")
  }

  isModeSheetOpen() {
    return this.hasModeSheetTarget && this.modeSheetTarget.classList.contains("is-open")
  }

  updateModeOptions() {
    if (!this.hasModeOptionTarget) return
    this.modeOptionTargets.forEach(option => {
      const selected = option.dataset.navSearchModeModeParam === this.modeValue
      option.classList.toggle("is-selected", selected)
      option.setAttribute("aria-selected", selected ? "true" : "false")
    })
  }

  isValidMode(mode) {
    return this.constructor.modes.includes(mode)
  }

  nextMode() {
    const currentIndex = this.constructor.modes.indexOf(this.modeValue)
    return this.constructor.modes[(currentIndex + 1) % this.constructor.modes.length]
  }

  isMobile() {
    return window.matchMedia("(max-width: 1199.98px)").matches
  }

  closeMobileNavMenu() {
    const collapse = document.getElementById("navbarSupportedContent")
    if (!collapse || !collapse.classList.contains("show")) return

    collapse.classList.remove("show", "collapsing")
    collapse.classList.add("collapse")
    collapse.style.removeProperty("height")
    document.querySelectorAll('[aria-controls="navbarSupportedContent"]').forEach((toggle) => {
      toggle.setAttribute("aria-expanded", "false")
      toggle.classList.add("collapsed")
    })
  }
}
